/**
 * @fileoverview The bed dialog: add a location, or change, archive or delete one.
 *
 * One dialog for both, because a new bed and an edited one ask the same
 * questions; the archive and delete controls only appear for a bed that
 * exists, and stay visible (greyed) while plants stand in the way, with the
 * way to clear the bed beneath them. Delete waits for no plants at all, archived
 * ones included (the server keeps every plant in a bed); the sentence beneath
 * counts the growing plants in the way, or else the archived ones. With only
 * archived plants left, Delete is not greyed: it opens a small dialog naming them
 * and saying what to do (Open Archived goes to /plants/archive, which has no
 * bed filter; OK closes it).
 *
 * The bed itself first: its name, photo and description; then what it is
 * (D55; reworked 2026-10-17 after the owner's critique: "I don't like how
 * there's more information hidden ... there's contradictions in there"):
 * every question its kind asks and only those, nothing folded away
 * (BedFactsFields, by the server's table, GET /locations/bed-questions; its
 * kind listed at once when adding, one row with Change when editing); then,
 * set apart under "This bed", Archive and Delete. It replaced the old
 * Sheltered switch: a cover that stays on all season, a cold frame, a
 * greenhouse or indoors is what the server keeps as sheltered. A ?more=1
 * link (moreOpen) still opens it; there is nothing folded to open now.
 *
 * The bed's photo is chosen with the same three ways as a plant's
 * (PhotoSourcePicker: camera, device, Google Photos), in a screen of its own
 * opened by "Add photo" / "Change photo"; the choice is then shrunk by
 * preparePhoto and previewed here, and sent when the bed is saved.
 *
 * A bed's cover, once it is a photo of the gardener's own (not the season's
 * picture), can be turned and cropped from here: "Edit image" beside "Change
 * photo" (ImageEditDialog). That is saved at once, on its own; the beds are
 * read again when this dialog closes.
 *
 * In someone else's garden (B37; owner, 2026-10-20, "b37 yes") a member adds
 * and edits beds, but only the owner deletes one or changes its sun and what's
 * over it (its shelter): for a member Delete is greyed with "Only the garden's
 * owner can change this." under it (useOwnerOnly), and BedFactsFields greys
 * those questions when editing (siteLockedReason). A save the server still
 * refuses for that reason (403 OWNER_ONLY: a change of kind that changes the
 * bed's sun or shelter) says the same words.
 *
 * Where: LocationGrid, from Add Location and from a bed's Edit details (or
 * a tile or row in the grid and list densities).
 * API: none for the form itself: onSave is LocationsContext's POST
 * /locations or PUT /locations/:id (multipart: name, description, the bed's
 * kind, sun, covers, cover_stays, hoops, watering and wind as its kind asks,
 * photo); GET /locations/bed-questions (useBedQuestions, only while the dialog is open: W24 Job H); the bed's
 * plants are the bed list's: its growing ones (plants) and the removed ones that keep it from being deleted
 * (archived_plants, W24 Job H); onArchive PATCH /locations/:id/status; onDelete DELETE
 * /locations/:id. The cover's own editor: PUT /locations/:id/photos/0/image
 * and PUT .../image/reset (editPhotoImageFor, resetPhotoImageFor), saved at
 * once.
 * Settings: the server's limits.LOCATION_NAME_MAX and limits.LOCATION_DESCRIPTION_MAX
 * (lib/growSettings); LOCATION_DESCRIPTION_COUNTER_FROM (config/tunables/garden).
 * Google Photos through useGooglePhotos (VITE_GOOGLE_CLIENT_ID).
 * Words (en/es): locations.form.* (newTitle, editTitle, name, description,
 * photo, photoAdd, photoChange, preparing,
 * nameRequired, nameTooLong, descriptionTooLong, saveFailed, archive*,
 * delete*, deleteKeepsArchived(One), deleteArchived*, archiveFirst(One), archiveAllLink, cancel, save); sitewide.saving (the loading Button's word).
 * Feet (B111): the dialog's foot and the archive and delete questions are ActionRow (Cancel or Keep it on the left, the action on the right, both 44px);
 * what waits on the server is Button `loading`.
 * plants.card.editImage; and
 * BedFactsFields' locations.facts.*; garden.ownerOnly.
 * Watch out: the form sends no status, so saving never un-archives a bed;
 * a cover photo stored as an address (a season's picture) cannot be edited.
 * Failures: saving, archiving or deleting that failed on our side (D31,
 * lib/serverFailure) says the standard sentence, reported to Sentry, with
 * the support address as a link; refusals keep their own words.
 *
 * @module EditLocationDialog
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { Button } from '@/shared/components/ui/button';
import { ActionRow } from '@/shared/components/ui/ActionRow';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Archive, Camera, Crop, Trash2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@/shared/hooks/useTranslation';
import type { Location } from '@/shared/api/locations';
import { preparePhoto } from '@/shared/lib/preparePhoto';
import axios from 'axios';
import { errorMessage } from '@/shared/lib/errorMessage';
import { serverFailureText } from '@/shared/lib/serverFailure';
import { FailureText } from '@/shared/components/ServerFailure';
import { editPhotoImageFor, resetPhotoImageFor } from '@/shared/api/photos';
import { locationSubject } from '@/shared/api/subject';
import { isEditedPhoto } from '@/shared/lib/imageEdit';
import { PhotoDialogShell } from '@/features/plant-list/components/dialogs/photo/PhotoDialogShell';
import { PhotoSourcePicker } from '@/features/plant-list/components/dialogs/photo/PhotoSourcePicker';
import { useCamera } from '@/features/plant-list/components/dialogs/photo/useCamera';
import { useGooglePhotos } from '@/features/plant-list/components/dialogs/photo/useGooglePhotos';
import { ImageEditDialog } from '@/features/plant-list/components/dialogs/photo/ImageEditDialog';
import { LOCATIONS_QUERY_KEY } from '@/config/tunables/ui';
import { BedFactsFields, NEW_BED_DRAFT, bedFormFields, useBedQuestions, type BedDraft } from './BedFactsFields';
import {
  LOCATION_DESCRIPTION_COUNTER_FROM,
} from '@/config/tunables/garden';
import { useGrowLimits } from '@/shared/lib/growSettings';
import { useOwnerOnly } from '@/shared/hooks/useOwnerOnly';

/**
 * Whether a request was refused because only the garden's owner may do it (403 OWNER_ONLY, B37).
 *
 * @param err - Whatever the request threw.
 * @returns True for that refusal.
 */
const refusedOwnerOnly = (err: unknown): boolean =>
  axios.isAxiosError(err) && err.response?.status === 403 && err.response?.data?.code === 'OWNER_ONLY';

/** The bed to edit (or none, to add one), and what each control does with it. */
interface EditLocationDialogProps {
  /** The location being edited, or null to create a new one. */
  location: Location | null;
  /** Whether the dialog is showing; each opening refills the form from location. */
  isOpen: boolean;
  /** Closes it (Cancel, a save, an archive or a delete done). */
  onClose: () => void;
  /** Receives multipart form data shaped for POST/PUT /locations. */
  onSave: (data: FormData) => Promise<unknown>;
  /** Removes the location. Only offered when it holds no plants, archived ones included. */
  onDelete?: (locationId: number) => Promise<unknown>;
  /** Retires the location. Refused while plants are still growing in it. */
  onArchive?: (locationId: number) => Promise<unknown>;
  /** Opens "Archive all" for this location: the way to empty it before archiving it. */
  onArchiveAll?: () => void;
  /** A ?more=1 link's: once opened "More about this bed", which is no longer folded (kept so old links still open the dialog). */
  moreOpen?: boolean;
}

/**
 * Create or edit a location.
 *
 * The backend takes multipart form data — name, description, shelter and an
 * optional photo file — so this builds FormData rather than JSON. Omitting the
 * photo leaves the existing one untouched.
 *
 * @param props - EditLocationDialogProps.
 */
export const EditLocationDialog: React.FC<EditLocationDialogProps> = ({
  location, isOpen, onClose, onSave, onDelete, onArchive, onArchiveAll,
}) => {
  const { reactTranslate: t } = useTranslation();
  const limits = useGrowLimits();
  const isNew = location === null;
  /** In someone else's garden: why Delete, the sun and what's over it are locked (B37); null in the person's own. */
  const { lockedReason } = useOwnerOnly();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  /** What the bed is (D55): kind, sun, and More about this bed; answers a kind does not ask are kept here, not sent. */
  const [bed, setBed] = useState<BedDraft>(NEW_BED_DRAFT);
  /** The server's table of what each kind of bed asks (GET /locations/bed-questions), read once the dialog opens; undefined until it is read. */
  const bedQuestions = useBedQuestions(isOpen);
  const [confirmingArchive, setConfirmingArchive] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [photo, setPhoto] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [isPreparing, setIsPreparing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  /** The cover as an edit here left it (its file and link), or null while it is as the bed came. */
  const [coverEdit, setCoverEdit] = useState<{ filename: string; url: string } | null>(null);
  /** True while the cover is being turned and cropped. */
  const [editingCover, setEditingCover] = useState(false);
  /** True while the photo source screen (camera, device, Google Photos) is open. */
  const [choosingPhoto, setChoosingPhoto] = useState(false);
  const camera = useCamera();
  // Kept with a Google Photos pick, so its photo still reaches the tray, on this bed, if the dialog closes first.
  const google = useGooglePhotos(choosingPhoto, { kind: 'bed', id: location?.id ?? null, locationId: location?.id ?? null });
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  /** True while the "still has archived plants" explanation is open. */
  const [explainingArchived, setExplainingArchived] = useState(false);

  // A bed that still holds any plant row cannot be deleted: every plant has a
  // bed (the column cannot be empty), and the server refuses rather than leave
  // a plant pointing at a bed that no longer exists. That includes archived
  // plants, which keep their bed for their history.
  // The bed list sends the two apart (W24 Job H): plants (growing) and archived_plants (removed).
  const livePlants = location?.plants?.length ?? 0;
  // Archiving is gentler: only plants still growing stand in the way.
  const archivable = livePlants === 0;
  /** This bed's removed plants: they do not block archiving the bed, only deleting it. */
  const archivedHere = location?.archived_plants ?? [];
  const archivedPlants = archivedHere.length;
  const plantCount = livePlants + archivedPlants;
  const deletable = plantCount === 0;
  // Delete on a bed holding only archived plants explains instead of greying out.
  const archivedOnly = plantCount > 0 && livePlants === 0;
  /** The archived plants' names, for the explanation: each its one name (the backend's names). */
  const archivedNames = archivedHere.map((plant) => plant.names.full);

  /**
   * Counts photo choices and openings, so a picture still being prepared
   * when the dialog closed (or another was chosen) is dropped when it is
   * ready, rather than becoming the next bed's cover.
   */
  const photoAttempt = useRef(0);

  // Refill whenever the dialog opens, so a cancelled edit leaves nothing behind.
  useEffect(() => {
    if (!isOpen) return;
    photoAttempt.current += 1;
    setIsPreparing(false);
    setName(location?.name ?? '');
    setDescription(location?.description ?? '');
    // A new bed has no facts yet: the draft's, with a sheltered one read as a cover that stays on.
    setBed(location?.bed ?? { ...NEW_BED_DRAFT, coverStays: location?.sheltered ?? false });
    setConfirmingArchive(false);
    setPhoto(null);
    // Start on the photograph the location already has. Showing nothing until
    // a new file was chosen meant editing a bed with a picture looked like a
    // bed with none, and the only way to find out was to save and see.
    setPreview(location?.signed_photo_url ?? null);
    setError(null);
    setIsSaving(false);
    setConfirmingDelete(false);
    setExplainingArchived(false);
    setIsDeleting(false);
    setCoverEdit(null);
    setEditingCover(false);
    setChoosingPhoto(false);
  }, [isOpen, location]);

  // The camera is released whenever the source screen closes.
  const { stop: stopCamera } = camera;
  useEffect(() => {
    if (!choosingPhoto) stopCamera();
  }, [choosingPhoto, stopCamera]);

  /*
   * A cover edited here is already saved; the beds are read again once the
   * dialog closes, not while it is open, because a new copy of this bed
   * would refill the form and lose whatever was being typed.
   */
  const coverChanged = coverEdit !== null;
  useEffect(() => {
    if (!isOpen && coverChanged) void queryClient.invalidateQueries({ queryKey: [LOCATIONS_QUERY_KEY] });
  }, [isOpen, coverChanged, queryClient]);

  /** The cover's stored file as it is now; null for none, or a season's picture (an address). */
  const coverFile = coverEdit?.filename ?? location?.location_photo_url ?? null;
  /** The cover can be turned and cropped: the bed's own photo, and no new one chosen to replace it. */
  const canEditCover = !isNew && !photo && Boolean(coverFile) && !/^https?:\/\//i.test(coverFile ?? '');

  /**
   * What an edit or a reset of the cover answered: shown here at once.
   *
   * @param answer - The cover's new file and link.
   * @param answer.filename - The file the bed now names.
   * @param answer.signed_photo_url - Its link.
   */
  const coverSaved = (answer: { filename: string; signed_photo_url?: string }): void => {
    const url = answer.signed_photo_url ?? null;
    setCoverEdit({ filename: answer.filename, url: url ?? '' });
    if (url) setPreview(url);
  };

  /**
   * Show the chosen picture straight away, and shrink it before it is sent.
   *
   * Neither happened. A phone photograph of a raised bed is routinely over
   * the five megabyte limit, so saving failed on the server with nothing on
   * screen to suggest why — and until then the dialog gave no sign a file had
   * been picked at all. Plant photographs have gone through this processor
   * for a while; location photographs never did.
   *
   * A picture that could not be made ready is not sent (preparePhoto, the one
   * rule for every picture): the picture the bed had stays and the reason is said.
   *
   * @param file - The picture chosen, or null when the choice was cleared.
   */
  const choosePhoto = async (file: File | null): Promise<void> => {
    // Only ever revoke what we made. The existing photo's URL belongs to the
    // server and revoking it would break the picture for everything else
    // holding it.
    /**
     * Revokes a preview URL, if it is one this dialog made.
     *
     * @param url - The preview's address.
     */
    const releasePreview = (url: string | null) => {
      if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
    };

    if (!file) {
      releasePreview(preview);
      setPhoto(null);
      setPreview(location?.signed_photo_url ?? null);
      return;
    }

    photoAttempt.current += 1;
    const mine = photoAttempt.current;
    setIsPreparing(true);
    setError(null);
    const prepared = await preparePhoto(file);
    // Closed and opened again, or another picture chosen, meanwhile.
    if (mine !== photoAttempt.current) return;
    if (!prepared.ok) {
      setError(prepared.message);
      setIsPreparing(false);
      return;
    }
    const ready = prepared.file;
    releasePreview(preview);
    setPhoto(ready);
    setPreview(URL.createObjectURL(ready));
    setIsPreparing(false);
  };

  // Object URLs are held by the browser until revoked, and a dialog reopened a
  // dozen times would leak every picture it had been shown. The server's own
  // URLs are left alone: they are not ours to release.
  useEffect(() => () => {
    if (preview?.startsWith('blob:')) URL.revokeObjectURL(preview);
  }, [preview]);

  /** What is wrong with the form, in words to show, or null when it can be saved. */
  const validate = (): string | null => {
    const trimmed = name.trim();
    if (!trimmed) return t('locations.form.nameRequired');
    if (limits.LOCATION_NAME_MAX !== undefined && trimmed.length > limits.LOCATION_NAME_MAX) return t('locations.form.nameTooLong');
    if (limits.LOCATION_DESCRIPTION_MAX !== undefined && description.length > limits.LOCATION_DESCRIPTION_MAX) return t('locations.form.descriptionTooLong');
    return null;
  };

  /** Saves the bed, new or changed, and closes; the server's own reason is shown when it refuses. */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }

    const data = new FormData();
    data.append('name', name.trim());
    data.append('description', description);
    // No status: a new bed starts live, and an edited one keeps the status it
    // has (the server reads it), so saving never un-archives a bed.
    // What the bed is, only what its kind asks (D55); before the questions are read, sheltered as before.
    if (bedQuestions.data) bedFormFields(bed, bedQuestions.data).forEach(([key, value]) => data.append(key, value));
    else if (location) data.append('sheltered', location.sheltered ? '1' : '0');
    if (photo) data.append('photo', photo);

    setIsSaving(true);
    setError(null);
    try {
      await onSave(data);
      onClose();
    } catch (err) {
      console.error('Error saving location:', err);
      // The server explains itself — "File too large", a name that is taken.
      // Replacing that with a generic failure threw away the only thing that
      // told the gardener what to do differently. errorMessage says it in the
      // reader's language, and keeps a 500's log message off the screen.
      setError(refusedOwnerOnly(err) ? t('garden.ownerOnly') : errorMessage(err, t('locations.form.saveFailed')));
    } finally {
      setIsSaving(false);
    }
  };

  /** Deletes the bed, after asking; refused while plants are in it, which is then said. */
  const handleDelete = async () => {
    if (!location || !onDelete || !deletable || lockedReason) return;
    setIsDeleting(true);
    setError(null);
    try {
      await onDelete(location.id);
      onClose();
    } catch (err) {
      console.error('Error deleting location:', err);
      // The backend refuses a location that still has plants. The dialog already
      // hides the control in that case, but the check here can be stale — a
      // plant may have been added since it opened — so report the real reason.
      const refusedForPlants =
        axios.isAxiosError(err) && err.response?.data?.error === 'LOCATION_HAS_PLANTS';
      setError(serverFailureText(err, 'locations.delete')
        ?? (refusedOwnerOnly(err) ? t('garden.ownerOnly') : null)
        ?? t(refusedForPlants ? 'locations.form.deleteBlocked' : 'locations.form.deleteFailed'));
      setConfirmingDelete(false);
    } finally {
      setIsDeleting(false);
    }
  };

  /** Archives the bed, after asking; refused while plants grow in it, which is then said. */
  const handleArchive = async () => {
    if (!location || !onArchive || !archivable) return;
    setIsArchiving(true);
    setError(null);
    try {
      await onArchive(location.id);
      onClose();
    } catch (err) {
      const refused =
        axios.isAxiosError(err) && err.response?.data?.error === 'LOCATION_HAS_LIVE_PLANTS';
      setError(serverFailureText(err, 'locations.archive')
        ?? t(refused ? 'locations.form.archiveBlocked' : 'locations.form.archiveFailed'));
      setConfirmingArchive(false);
    } finally {
      setIsArchiving(false);
    }
  };

  // Saving while the picture is still being shrunk would send the original.
  const busy = isSaving || isDeleting || isArchiving || isPreparing;

  return (
    // Not closed by Escape or a tap outside while a save, archive or delete is
    // under way: a failure would be said in a dialog no longer there.
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open && !isSaving && !isArchiving && !isDeleting) onClose(); }}>
      {/*
        * The same shape as Add a plant: the whole screen on a phone, with the
        * title pinned at the top and Cancel and Save pinned at the bottom, and
        * a box of its own size from sm up.
        */}
      <DialogContent
        scrollBody={false}
        className={cn(
          'flex h-[100dvh] max-h-none flex-col gap-0 overflow-hidden rounded-none border-0 p-0 max-sm:left-0 max-sm:top-0 max-sm:translate-x-0',
          'sm:h-auto sm:max-h-[90dvh] sm:max-w-lg sm:rounded-lg sm:border',
        )}
      >
        <DialogHeader className="min-h-[3.5rem] shrink-0 justify-center space-y-0 border-b px-4 py-2 pr-14 text-left">
          <DialogTitle className="font-body">
            {isNew ? t('locations.form.newTitle') : t('locations.form.editTitle')}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 pb-4 pt-3">
            <div className="space-y-1.5">
              <Label htmlFor="location-name">{t('locations.form.name')}</Label>
              <Input
                id="location-name"
                value={name}
                maxLength={limits.LOCATION_NAME_MAX}
                onChange={(e) => setName(e.target.value)}
                disabled={busy}
                autoFocus
              />
            </div>

            {/* The bed itself, before what it is: its photo and description (owner, 2026-10-17). */}
            <div className="space-y-1.5">
              <Label htmlFor="location-description">{t('locations.form.description')}</Label>
              <textarea
                id="location-description"
                value={description}
                maxLength={limits.LOCATION_DESCRIPTION_MAX}
                rows={2}
                onChange={(e) => setDescription(e.target.value)}
                disabled={busy}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50"
              />
              {/* Only once it is close, so it reads as a warning not decoration. */}
              {limits.LOCATION_DESCRIPTION_MAX !== undefined && description.length >= limits.LOCATION_DESCRIPTION_MAX - LOCATION_DESCRIPTION_COUNTER_FROM && (
                <p className="text-xs tabular-nums text-muted-foreground">
                  {limits.LOCATION_DESCRIPTION_MAX - description.length}
                </p>
              )}
            </div>

            {/* The same kind of button a plant card has for a picture, not the
                browser's own "Choose File". The thumbnail shows which picture
                is attached; the input itself is hidden behind the button. */}
            <div className="space-y-1.5">
              <Label>{t('locations.form.photo')}</Label>
              <div className="flex flex-wrap items-center gap-3">
                <Button type="button" variant="outline" disabled={busy} onClick={() => setChoosingPhoto(true)}>
                  <Camera className="mr-2 h-4 w-4" aria-hidden="true" />
                  {preview ? t('locations.form.photoChange') : t('locations.form.photoAdd')}
                </Button>
                {canEditCover && (
                  <Button type="button" variant="outline" onClick={() => setEditingCover(true)} disabled={busy}>
                    <Crop className="mr-2 h-4 w-4" aria-hidden="true" />
                    {t('plants.card.editImage')}
                  </Button>
                )}
                {preview && (
                  <img
                    src={preview}
                    alt=""
                    className="h-12 w-12 shrink-0 rounded-sm border object-cover"
                  />
                )}
              </div>
              {isPreparing && (
                <p className="text-xs text-muted-foreground">{t('locations.form.preparing')}</p>
              )}
            </div>

            {/* What the bed is: every question its kind asks, nothing folded (D55; owner, 2026-10-17). */}
            <BedFactsFields
              questions={bedQuestions.data}
              value={bed}
              onChange={setBed}
              mode={isNew ? 'add' : 'edit'}
              disabled={busy}
              siteLockedReason={isNew ? null : lockedReason}
            />


            {error && (
              <p role="alert" className="text-sm text-destructive"><FailureText text={error} /></p>
            )}

            {/*
              * Archive and delete, as buttons even when they cannot be pressed.
              * They used to vanish behind a grey sentence while the location
              * held plants, so the pencil led to an explanation instead of the
              * thing the gardener came for. Greyed, with the way to clear the
              * location right underneath, they still say what is possible.
              */}
            {!isNew && (onArchive || onDelete) && (
              <div className="space-y-2 border-t pt-4">
                {/* Set apart from the bed's questions under its own heading (owner, 2026-10-17). */}
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('locations.form.thisBed')}</p>
                {confirmingArchive ? (
                  <div className="space-y-2">
                    <p className="text-sm">{t('locations.form.archiveConfirm')}</p>
                    <ActionRow onCancel={() => setConfirmingArchive(false)} cancelLabel={t('locations.form.deleteCancel')} disabled={busy}>
                      <Button type="button" onClick={handleArchive} disabled={busy} loading={isArchiving} busy="archiving">
                        {t('locations.form.archiveConfirmYes')}
                      </Button>
                    </ActionRow>
                  </div>
                ) : confirmingDelete ? (
                  <div className="space-y-2">
                    <p className="text-sm">{t('locations.form.deleteConfirm')}</p>
                    <ActionRow onCancel={() => setConfirmingDelete(false)} cancelLabel={t('locations.form.deleteCancel')} disabled={busy}>
                      <Button type="button" variant="destructive" onClick={handleDelete} disabled={busy} loading={isDeleting} busy="deleting">
                        {t('locations.form.deleteConfirmYes')}
                      </Button>
                    </ActionRow>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      {onArchive && (
                        <Button type="button" variant="outline" onClick={() => setConfirmingArchive(true)} disabled={busy || !archivable}>
                          <Archive className="mr-1.5 h-4 w-4" aria-hidden="true" />
                          {t('locations.form.archive')}
                        </Button>
                      )}
                      {onDelete && (
                        <Button type="button" variant="outline" className="text-destructive" onClick={() => (archivedOnly ? setExplainingArchived(true) : setConfirmingDelete(true))} disabled={busy || Boolean(lockedReason) || (!deletable && !archivedOnly)} title={lockedReason ?? undefined}>
                          <Trash2 className="mr-1.5 h-4 w-4" aria-hidden="true" />
                          {t('locations.form.delete')}
                        </Button>
                      )}
                    </div>
                    {/* A member: only the owner deletes a bed (B37). */}
                    {onDelete && lockedReason && <p className="text-sm text-muted-foreground">{lockedReason}</p>}
                    {/* Only when plants really stand in the way, and how many: growing ones first, else archived ones. */}
                    {livePlants > 0 ? (
                      <p className="text-sm text-muted-foreground">
                        {livePlants === 1
                          ? t('locations.form.archiveFirstOne')
                          : t('locations.form.archiveFirst', { count: livePlants })}{' '}
                        {onArchiveAll && (
                          <button type="button" onClick={onArchiveAll} disabled={busy} className="font-medium text-primary hover:underline">
                            {t('locations.form.archiveAllLink')}
                          </button>
                        )}
                      </p>
                    ) : archivedPlants > 0 && onDelete && (
                      <p className="text-sm text-muted-foreground">
                        {archivedPlants === 1
                          ? t('locations.form.deleteKeepsArchivedOne')
                          : t('locations.form.deleteKeepsArchived', { count: archivedPlants })}
                      </p>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          <div className="shrink-0 border-t px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            <ActionRow onCancel={onClose} cancelLabel={t('locations.form.cancel')} disabled={busy}>
              <Button type="submit" disabled={busy} loading={isSaving}>
                {t('locations.form.save')}
              </Button>
            </ActionRow>
          </div>
        </form>
      </DialogContent>
      {/* Why a bed with only archived plants cannot be deleted yet, and where to go. */}
      <Dialog open={explainingArchived} onOpenChange={setExplainingArchived}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {archivedPlants === 1
                ? t('locations.form.deleteArchivedTitleOne')
                : t('locations.form.deleteArchivedTitle', { count: archivedPlants })}
            </DialogTitle>
            <DialogDescription>{t('locations.form.deleteArchivedBody')}</DialogDescription>
          </DialogHeader>
          <ul className="max-h-48 list-disc space-y-1 overflow-y-auto pl-5 text-sm">
            {archivedNames.map((label, index) => <li key={index}>{label}</li>)}
          </ul>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => { setExplainingArchived(false); onClose(); navigate('/plants/archive'); }}>
              {t('locations.form.deleteArchivedOpen')}
            </Button>
            <Button type="button" onClick={() => setExplainingArchived(false)}>
              {t('locations.form.deleteArchivedOk')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* The same three ways in as a plant's photo: camera, device, Google Photos. */}
      <PhotoDialogShell
        isOpen={choosingPhoto}
        onClose={() => setChoosingPhoto(false)}
        title={preview ? t('locations.form.photoChange') : t('locations.form.photoAdd')}
      >
        <PhotoSourcePicker
          camera={camera}
          onCaptured={(file) => { setChoosingPhoto(false); void choosePhoto(file); }}
          onFileChosen={(file) => { setChoosingPhoto(false); void choosePhoto(file); }}
          onPickGoogle={google.pick}
          isPickingGoogle={google.isPicking}
          googleStatus={google.status}
          googleCount={google.count}
          onCancelGoogle={google.cancel}
          googleError={google.error}
        />
      </PhotoDialogShell>
      {/* The cover's own editor, saved on its own. */}
      {location && (
        <ImageEditDialog
          isOpen={editingCover}
          onClose={() => setEditingCover(false)}
          src={coverEdit?.url || location.signed_photo_url}
          alt={location.name}
          edited={isEditedPhoto(coverFile)}
          onSave={async (edit) => coverSaved(await editPhotoImageFor(locationSubject(location.id), 0, edit))}
          onReset={async () => coverSaved(await resetPhotoImageFor(locationSubject(location.id), 0))}
        />
      )}
    </Dialog>
  );
};
